export interface IGiftCard {
  id: string;
  code: string;
  balance: number;
  origin: string;
  originalAmount: number;
  locationId: string;
  businessId: string;
  canBeUsedAtOtherLocations: boolean;
  canBeUsedAtOtherBusinesses: boolean;
  createdUtc: Date;
  expires?: Date;
  issued: Date;
  lastUsedUtc: Date;
  from: string;
  to: string;
  message: string;
  isExpired: boolean;
  transactions: IGiftCardTransaction[];
}

export interface IGiftCardForm {
  id: string;
  code: string;
  amount: number;
  canBeUsedAtOtherLocations: boolean;
  expires?: Date;
  from: string;
  to: string;
  message: string;
}

export enum BalanceOperation {
  Increase,
  Decrease,
}

export interface IGiftCardTransaction {
  id: string;
  orderId: string;
  customerId: string;
  amount: number;
  balanceOperation: BalanceOperation;
  capturedUtc: Date;
}

/**
 * Maximum number of distinct gift cards that can be applied to a single order.
 * Mirrors OrderConstants.MaxGiftCardsPerOrder in app-spaccurate-order-api.
 */
export const MAX_GIFT_CARDS_PER_ORDER = 3;

/**
 * One page of a server-paged read (#2220). Same field names as the platform's
 * Spaccurate.Framework.Pagination.Paged<T> envelope, which credit-ledger's PagedResult<T> mirrors.
 */
export interface IPaged<T> {
  items: T[];
  currentPage: number;
  resultsPerPage: number;
  totalPages: number;
  totalResults: number;
}

/**
 * Rows per page of the Gift Cards grid. The server pages at this size (#2220); it is also the
 * default credit-ledger applies when the parameter is omitted, and the grid has always drawn 20.
 */
export const GIFT_CARD_PAGE_SIZE = 20;

export const BLANK_GIFT_CARD: IGiftCard = {
  id: '',
  code: '',
  balance: 0,
  origin: '',
  originalAmount: 0,
  locationId: '',
  businessId: '',
  canBeUsedAtOtherLocations: false,
  canBeUsedAtOtherBusinesses: false,
  createdUtc: new Date(),
  issued: new Date(),
  lastUsedUtc: new Date(),
  from: '',
  to: '',
  message: '',
  isExpired: false,
  transactions: [],
};

export interface IGiftCardBulkConfigForm {
  businessId: string;
  locationId: string;
  canBeUsedAtOtherLocations: boolean;
  origin: string;
}

export interface IGiftCardFileUploadForm extends IGiftCardBulkConfigForm {
  file: File;
}

export const BLANK_GIFT_CARD_CSV_FORM: IGiftCardBulkConfigForm = {
  businessId: '',
  locationId: '',
  canBeUsedAtOtherLocations: true,
  origin: '',
};

/**
 * The Issue Store Credit form (#2381): one card the business gives away and funds itself, issued through
 * credit-ledger's POST /credit-ledger/gift-card/business (#2380). `expires` is the date input's
 * YYYY-MM-DD value, blank for no expiry.
 */
export interface IIssueStoreCreditForm {
  locationId: string;
  amount: number | '';
  code: string;
  expires: string;
  from: string;
  to: string;
  message: string;
  reason: string;
  canBeUsedAtOtherLocations: boolean;
}

export const BLANK_ISSUE_STORE_CREDIT_FORM: IIssueStoreCreditForm = {
  locationId: '',
  amount: '',
  code: '',
  expires: '',
  from: '',
  to: '',
  message: '',
  reason: '',
  canBeUsedAtOtherLocations: true,
};

export interface IPurchaseGiftCardForm {
  type: 'Digital' | 'Physical' | null;
  amount: number | '';
  // Digital
  senderName: string;
  recipientName: string;
  recipientEmail: string;
  message: string;
  // Physical
  customCode: string;
  locationId: string;
  canBeUsedAtOtherLocations: boolean;
  // From Response
  id: string;
  code: string;
}

export const BLANK_PURCHASE_GIFT_CARD_FORM: IPurchaseGiftCardForm = {
  type: null,
  amount: '',
  senderName: '',
  recipientName: '',
  recipientEmail: '',
  message: '',
  customCode: '',
  locationId: '',
  canBeUsedAtOtherLocations: true,
  id: '',
  code: '',
};

export interface IGiftCardLineItem {
  name: string;
  price: number;
  description: string;
  type: string;
}

export interface IGiftCardOrderDetails {
  id: string;
  clientSecret: string;
}

export interface IGiftCardCustomerDetails {
  name: string;
  email: string;
}

export const BLANK_GIFT_CARD_DETAILS: IGiftCardCustomerDetails = {
  name: '',
  email: '',
};
